import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import useGetHook from "../../../customHooks/useGetHook";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useDebounce } from "../../../customHooks/useDebounceHook";

export default function ContactCombobox(props: any) {
  const {
    url,
    queryKey,
    props: otherProps,
    errors,
    setValue,
    name,
    watch,
    clearErrors,
    disabled = false,
    focus = false,
    placeholder,
    label,
  } = props;

  const limit = 10;
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const debouncedSearch = useDebounce(query);

  const { data: serverData } = useGetHook({
    queryKey: [queryKey, `${queryKey}-${limit}-${debouncedSearch}`],
    url: url,
    params: {
      limit,
      offset: 0,
      search: debouncedSearch,
      ...otherProps,
    },
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData(serverData.results);
    }
  }, [serverData]);

  useEffect(() => {
    setValue(name, selected?.phone_number ?? null);
  }, [selected]);

  useEffect(() => {
    const currentValue = watch(name);
    if (!selected && currentValue) {
      const match = data.find((item: any) => item.id === currentValue);
      if (match) setSelected(match);
    } else if (!currentValue) {
      setSelected(null);
    }
  }, [watch(name), data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleFocusOutside = () => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(document.activeElement as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("focusin", handleFocusOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleFocusOutside);
    };
  }, []);

  const filteredData: any =
    query === ""
      ? data
      : data.filter((item: any) =>
          item?.phone_number
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <Combobox
      value={selected}
      disabled={disabled}
      onChange={(value) => {
        setSelected(value);
        setIsOpen(false);
      }}
    >
      <div ref={comboboxRef} className={tw`relative`}>
        <label>{label}</label>
        <div
          className={tw`relative w-full cursor-default overflow-hidden bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm mt-2`}
        >
          <Combobox.Input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            className={tw`w-full focus:outline-none bg-[#EEF5F4] p-[10px] text-left`}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus={focus}
            displayValue={(item: any) => item?.phone_number}
            onChange={(event) => {
              const numericValue = event.target.value.replace(/\D/g, "");
              setQuery(numericValue);
              setIsOpen(true);
            }}
            onClick={() => setIsOpen(true)}
            onBlur={() => clearErrors(name)}
            onKeyDown={(e) => {
              const allowedKeys = [
                "Backspace",
                "Delete",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
              ];
              if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
          <Combobox.Button
            ref={comboboxButtonRef}
            className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {selected && (
              <PlusIcon
                className={tw`h-5 w-5 text-gray-400 rotate-45`}
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(null);
                  setValue(name, "");
                }}
              />
            )}
            <ChevronDownIcon
              className={tw`h-5 w-5 text-gray-400`}
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>

        <Transition
          as={Fragment}
          show={isOpen}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options
            static
            className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
          >
            {query?.length > 0 && (
              <Combobox.Option
                value={{ id: null, phone_number: query }}
                className={({ active }) =>
                  tw`relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? tw`bg-primary text-white` : tw`text-gray-900`
                  }`
                }
              >
                {({ selected, active }) => (
                  <span
                    className={tw`${selected ? `font-medium` : `font-normal`}`}
                  >
                    Create "{query}"
                  </span>
                )}
              </Combobox.Option>
            )}
            {filteredData.map((item: any) => (
              <Combobox.Option
                key={item.id}
                className={({ active }) =>
                  tw`relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? tw`bg-primary text-white` : tw`text-gray-900`
                  }`
                }
                value={item}
              >
                {({ selected }) => (
                  <span
                    className={tw`block truncate ${
                      selected ? tw`font-medium` : tw`font-normal`
                    }`}
                  >
                    {item.phone_number}, {item.full_name}
                  </span>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>

        {errors && (
          <p className={tw`text-red-500 text-xs my-1`}>{errors?.message}</p>
        )}
      </div>
    </Combobox>
  );
}
